import { useState, useEffect, useCallback } from 'react';
import { requestNotificationPermission, sendNotification, stopNotificationSound } from '../utils/notifications';
import NotificationToast from '../components/admin/NotificationToast';

/**
 * Hook for managing admin notifications
 * Handles browser notifications, toast notifications, sound alerts, and notification center
 */
export const useAdminNotifications = () => {
  const [toasts, setToasts] = useState([]);
  const [notifications, setNotifications] = useState(() => {
    // Load from localStorage
    try {
      const saved = localStorage.getItem('admin-notifications');
      return saved ? JSON.parse(saved) : [];
    } catch (error) {
      return [];
    }
  });
  const [browserPermission, setBrowserPermission] = useState(() => {
    if (typeof Notification !== 'undefined') {
      return Notification.permission || 'default';
    }
    return 'default';
  });

  // Save notifications to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('admin-notifications', JSON.stringify(notifications));
    } catch (error) {
      // Ignore storage errors (private mode or blocked storage).
    }
  }, [notifications]);

  // Request browser notification permission on mount
  useEffect(() => {
    const checkPermission = async () => {
      if (typeof window !== 'undefined' && 'Notification' in window) {
        setBrowserPermission(Notification.permission);
        
        // Auto-request permission if not set
        if (Notification.permission === 'default') {
          const granted = await requestNotificationPermission();
          setBrowserPermission(granted ? 'granted' : 'denied');
        }
      }
    };

    checkPermission();
  }, []);

  // Show notification (browser + toast + sound + notification center)
  const notify = useCallback(async (notificationType, data, customOptions = {}) => {
    const notification = await sendNotification(notificationType, data, customOptions);
    
    if (notification) {
      // Add to notification center (unread)
      setNotifications(prev => [notification, ...prev]);
      
      // Add toast notification
      setToasts(prev => [...prev, notification]);
    }
  }, []);

  // Show custom notification
  const notifyCustom = useCallback(async (title, message, type = 'info', options = {}) => {
    const { notifyAdmin } = await import('../utils/notifications');
    const notification = await notifyAdmin({
      title,
      message,
      type,
      ...options,
    });

    if (notification) {
      setNotifications(prev => [notification, ...prev]);
      setToasts(prev => [...prev, notification]);
    }
  }, []);

  // Mark notification as seen (stops sound)
  const markAsSeen = useCallback((id) => {
    setNotifications(prev => 
      prev.map(n => {
        if (n.id === id && !n.seen) {
          // Stop sound when marked as seen
          if (n.soundLoopId) {
            stopNotificationSound(n.soundLoopId);
          }
          return { ...n, seen: true };
        }
        return n;
      })
    );
  }, []);

  // Clear all notifications
  const clearAll = useCallback(() => {
    // Stop all sounds
    notifications.forEach(notification => {
      if (notification.soundLoopId) {
        stopNotificationSound(notification.soundLoopId);
      }
    });
    setNotifications(prev => prev.map(n => ({ ...n, seen: true })));
  }, [notifications]);

  // Remove toast
  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  // Toast component renderer
  const ToastContainer = () => (
    <div className="notification-toast-container">
      {toasts.map(toast => (
        <NotificationToast
          key={toast.id}
          notification={toast}
          onClose={() => removeToast(toast.id)}
        />
      ))}
    </div>
  );

  return {
    notify,
    notifyCustom,
    removeToast,
    markAsSeen,
    clearAll,
    notifications,
    ToastContainer,
    browserPermission,
    requestPermission: async () => {
      if (typeof window === 'undefined' || !('Notification' in window)) {
        setBrowserPermission('denied');
        return false;
      }

      const granted = await requestNotificationPermission();
      setBrowserPermission(granted ? 'granted' : 'denied');
      return granted;
    },
  };
};
